import React from 'react'
import { Select } from 'antd'
import { FormattedMessage } from 'react-intl'
import {
  e as nonEmptyCellValue,
  validateEmail,
} from 'components/GeneralComponents/ExcelLikeTable/helpers'
import xirr from 'xirr'
import { arrToObj, groupBy, hasRemoteId, onlyUniqueKey, get, equalsDouble, nonNull } from 'utils'
import _ from 'lodash'
import moment from 'moment'
import { date, money } from './format'
import { shareholderOrder } from './enums'

export const byDate = (keyGetter) => (e1, e2) => date(keyGetter(e1)).diff(date(keyGetter(e2)))

const today = moment(new Date()).endOf('day')
export const getDefaultExitDate = (round) => {
  const roundDate = moment(round.date)
  return today.isBefore(roundDate) ? roundDate : today
}

const getSharePointName = (sp, shareGroupsById, shareGroupsByGroupId, getGroupId) =>
  `${sp.share.name} ${
    shareGroupsById.get(sp.share.id).length > 1
      ? `(${shareGroupsByGroupId
          .get(getGroupId(sp))
          .flatMap((s) => s.preferenceAmounts.map((i) => i.investor.name))
          .join(', ')})`
      : ''
  }`

// Summarize almost identical conversion/cap points for investors which paid similar prices
export const summarizedSharePoints = (sharePoints) => {
  const getGroupId = (sp) => `${sp.share.id}-${money(sp.conversionPoint)}-${money(sp.capPoint)}`
  const shareGroupsById = groupBy(sharePoints, (sr) => sr.share.id)
  const shareGroupsByGroupId = groupBy(sharePoints, (sp) => getGroupId(sp))

  return sharePoints
    .filter(onlyUniqueKey(getGroupId))
    .sort((sp1, sp2) => byShares(sp1.share, sp2.share))
    .map((sp) => {
      return {
        ...sp,
        name: getSharePointName(sp, shareGroupsById, shareGroupsByGroupId, getGroupId),
      }
    })
}

// Summarize almost identical conversion/cap points for investors which paid similar prices
export const summarizedConversionPoints = (sharePoints) => {
  const getGroupId = (sp) => `${sp.share.id}-${money(sp.conversionPoint)}-${money(sp.capPoint)}`
  const getCPId = (sp) => `${money(sp.conversionPoint)}`
  const shareGroupsById = groupBy(sharePoints, (sr) => sr.share.id)
  const shareGroupsByCPId = groupBy(sharePoints, (sp) => getCPId(sp))
  const shareGroupsByGroupId = groupBy(sharePoints, (sp) => getGroupId(sp))

  return sharePoints
    .filter((s) => !!s.conversionPoint)
    .filter(onlyUniqueKey(getCPId))
    .sort((s1, s2) => s1.conversionPoint - s2.conversionPoint)
    .map((sp) => {
      const names = shareGroupsByCPId
        .get(getCPId(sp))
        .filter(onlyUniqueKey((s) => s.share.id))
        .map((s) => getSharePointName(s, shareGroupsById, shareGroupsByGroupId, getGroupId))

      return { ...sp, names }
    })
}

export const isValidVestingSchedule = (vestingSchedule) => {
  const { schedule, cliffPercent, cliff } = vestingSchedule
  const validCliff = nonEmptyCellValue(cliff) === nonEmptyCellValue(cliffPercent)
  const validSchedule = schedule.every(
    (p) =>
      nonEmptyCellValue(p.percent) &&
      nonEmptyCellValue(p.vestingTime) &&
      nonEmptyCellValue(p.months),
  )

  const vestingPercent = schedule.reduce((t, p) => t + p.percent, 0.0)
  const totalPercent = (cliffPercent || 0) + vestingPercent
  const isValidVestingPlan = validSchedule && validCliff && equalsDouble(totalPercent, 1.0)
  return isValidVestingPlan
}

// Get all transactions of all investors. Format: { amount, when, who }
export const getTransactions = (participations) => {
  // Investments: EquityInvestment, ConvertibleNote, AD-Event (capital increase), BuyOut
  // Payouts: PayOut, LP/Dividends/Option, Exit
  // Edge-cases: (1) No Investments but payouts (e.g. Payout/Option)
  //             (2) All the same date

  const transactions = participations
    .flatMap((p) => p.entries)
    .filter(
      (e) =>
        ['EQUITY_INVESTMENT', 'CN_INVESTMENT'].includes(e.event.type) ||
        // Filter out duplicate entry, as we have already the Exit-Return entry for the seller and are not interested in the shares
        (e.event.type === 'BUY_OUT' &&
          !(e.type === 'SHARE' && e.event.seller.id === e.investor.id)) ||
        (e.event.type === 'SIM_ANTI_DILUTION' && e.event.capitalIncrease > 0),
    )
    .sort(byDate((e) => e.round.date))
    .map((e) => {
      const when = date(e.round.date).toDate()
      const who = e.investor
      const { type } = e.event
      switch (type) {
        case 'EQUITY_INVESTMENT':
          return { amount: -e.event.amount, when, who, type }
        case 'CN_INVESTMENT':
          return { amount: -e.event.amount, when, who, type }
        case 'SIM_ANTI_DILUTION':
          return { amount: -e.event.capitalIncrease, when, who, type }
        case 'BUY_OUT': {
          const isBuyer = e.event.buyer.id === e.investor.id
          const mult = isBuyer ? -1 : +1
          return { amount: mult * e.event.amount, when, who, type }
        }
        default:
          return { amount: 0, when, who, type }
      }
    })

  return transactions
}

// Calculate the XIRR based on all transactions (for the specific investor return)
export const XIRR = (transactions, investorReturns, exitDate) => {
  transactions = groupBy(transactions, (i) => i.who.id)
  let irrs = Array.from(transactions.keys()).map((key) => {
    const investorTransactions = transactions.get(key)
    const investorReturn = investorReturns.find((ir) => ir.investor.id === key)
    investorTransactions.push({
      amount: investorReturn ? investorReturn.amount : 0.0,
      when: date(exitDate).toDate(),
    })
    try {
      const irr = xirr(investorTransactions)
      return { irr, investorId: key }
    } catch (error) {
      return { investorId: key, error }
    }
  })

  irrs = arrToObj(
    irrs,
    (i) => i.investorId,
    (v) => ({
      irr: v.irr,
      transactions: transactions.get(v.investorId) || [],
      error: v.error,
    }),
  )

  return irrs
}

export const getQuota = (participation, participations, shareMode) => {
  const totalNumberOfShares = participations.reduce((t, p) => t + getAmount(p, shareMode), 0)
  return getAmount(participation, shareMode) / totalNumberOfShares
}

export const getGrantProceedsPerShare = (g, csOptionValue) => {
  return csOptionValue > get('strikePrice', g, 0) ? csOptionValue - get('strikePrice', g, 0) : 0
}

export const getGrantProceedsFullyVested = (g, csOptionValue) => {
  return g.shareAmount * getGrantProceedsPerShare(g, csOptionValue)
}

export const getAmount = (participation, shareMode) => {
  if (!['ESOP', 'GRANTED_SHARE', 'SHARE'].includes(participation.type)) return participation.amount

  const type = typeof shareMode === 'string' ? shareMode : shareMode.type
  switch (type) {
    // Virtual share distribution: fully-diluted. Including granted shares and ESOP.
    case 'esop':
      return participation.amountEsop
    // Virtual share distribution: At exit, fully-diluted including vesting and acceleration.
    case 'exit':
      return participation.amountExit
    // Legal share distribution with vesting
    case 'vested':
      return ['ESOP', 'GRANTED_SHARE'].includes(participation.type) ? 0 : participation.amountVested
    // Legal share distribution: Only regular shares have an amount (NOTE: participation.amount for backwards compatibility with entries)
    case 'shares':
    default:
      return ['ESOP', 'GRANTED_SHARE'].includes(participation.type)
        ? 0
        : participation.amountLegal || participation.amount
  }
}

export const getAutoCalculationPrice = (calculationMethod, capTable) => {
  switch (calculationMethod) {
    case 'AUTO_PRE_MONEY_FD':
      return capTable.preMoneyBasedPricePerShareFD
    case 'AUTO_PRE_MONEY_ND':
      return capTable.preMoneyBasedPricePerShareND
    case 'AUTO_PRE_MONEY_INC_CNS_FD':
      return capTable.preMoneyBasedPricePerShareWithRoundCNsFD
    case 'AUTO_PRE_MONEY_INC_CNS_ND':
      return capTable.preMoneyBasedPricePerShareWithRoundCNsND
    case 'AUTO_PRE_MONEY_AS_IF_FD':
      return capTable.preMoneyAsIfConvertedBasedPricePerShareFD
    default:
      return capTable.preMoneyBasedPricePerShareFD
  }
}

export const getVestingEndDate = (eventDate, vestingSchedule) => {
  if (!vestingSchedule) return eventDate
  const { cliff, schedule } = vestingSchedule
  const totalMonths = cliff + schedule.reduce((t, p) => t + p.months, 0)
  return date(eventDate).add(totalMonths, 'month')
}

// Similar vesting-function to backend implementation
export const applyVesting = (
  shareAmount,
  vestingSchedule,
  eventDate,
  endDate = new Date(),
  exit = false,
) => {
  if (!vestingSchedule) return shareAmount
  const startDate = vestingSchedule.startDate || eventDate
  if (date(startDate).isAfter(date(endDate))) return 0

  const {
    cliff,
    schedule,
    cliffPercent,
    accelerationOnExit,
    percentUnderlyingVesting,
  } = vestingSchedule
  if (exit && accelerationOnExit) return shareAmount

  const vestingAmount = percentUnderlyingVesting
    ? Math.round(percentUnderlyingVesting * shareAmount)
    : shareAmount
  const totalMonths = date(endDate).diff(date(startDate), 'months')
  if ((cliff && totalMonths < cliff) || totalMonths < 1) return shareAmount - vestingAmount

  let time = totalMonths - cliff
  let vestedPercent = cliffPercent

  // Use Array#some , to interrupt when returned true (similar to "break" in for-loop)
  schedule.some((s) => {
    const { months, vestingTime, percent } = s
    // Avoid errors on edge-cases
    if (months === 0 || vestingTime === 0) {
      vestedPercent += percent
    } else {
      // 1.) Apply vestingTime - interval period
      const flooredTotalTime = Math.floor(months / vestingTime)
      const flooredTime = Math.min(time / vestingTime, flooredTotalTime) // Maximal the full period

      // 2.) weight percent proportional to months passed
      vestedPercent += percent * (flooredTime / flooredTotalTime)

      // 3.) Check if it run out of time during this period
      if (flooredTime < flooredTotalTime) return true
      time -= months
    }
    return false
  })
  // Remove rounding errors
  vestedPercent = _.round(vestedPercent, 8)

  return Math.floor(vestedPercent * vestingAmount) + (shareAmount - vestingAmount)
}

export const filterByShareMode = (object, shareMode) => {
  const isParticipation = !object.event // Workaround, because participations don't have the field
  const isEntry = !!object.event // entries always have the field

  if (!(isParticipation || isEntry)) return false
  if (getAmount(object, shareMode) === 0) return false // and has an amount in current context (or simply 0 because 0 investment -> ignore)

  return (
    (object.type === 'ESOP' && shareMode.type === 'esop') || // if esop selected -> include
    (object.type === 'GRANTED_SHARE' &&
      ['vested', 'granted', 'esop', 'exit'].includes(shareMode.type)) || // if vested, granted selected -> include
    object.type === 'SHARE' // or normal share
  )
}

export const filterByNonInvestors = (stakeholder) => {
  return !['ESOP_POOL', 'ESOP'].includes(stakeholder.type)
}

export const filterCommonShareOptions = (share) => {
  // TODO: Compare to project.commonShareOptions
  return !(share.commonShare && share.name === 'Common Shares (Options)')
}

export const countOwnProjects = (projects, userState) => {
  // on loading assume at least one project so safety checks do not fail
  if (!projects || !userState) return 1

  let ownProjects = 0
  /* eslint-disable no-restricted-syntax */
  for (const { projectMemberships } of projects) {
    for (const membership of projectMemberships) {
      if (
        membership.userAccount.userId === userState.userId &&
        membership.projectRole === 'OWNER'
      ) {
        ownProjects++
      }
    }
  }
  /* eslint-enable no-restricted-syntax */
  return ownProjects
}

export const entryFilter = (entry) => {
  const { event, operation } = entry

  // Exclude Virtual ESOP Pool Backing
  if (event.type === 'ESOP_CREATION' && operation === 'SUB') return false

  // Exclude dividends (only relevant for internals)
  if (event.type === 'SIM_DIVIDENDS') return false

  return true
}

/**
 * a helping list for investors getting grouped correctly
 *  + defining keys for sorting, more types can be dynamically added
 *  - NOTE this could be error-prone if we change names of the types
 * */
export const groupShareholderOptions = (shareholders, types) => {
  const investors = []

  const groupedInvestors = Object.assign({}, ...types.map((t) => ({ [t]: [] })))

  shareholders.sort(byStakeholders).forEach((shareholder) => {
    const { type, id, name } = shareholder

    if (!groupedInvestors[type]) groupedInvestors[type] = []

    groupedInvestors[type].push(
      <Select.Option key={id} value={id} label={name}>
        {name}
      </Select.Option>,
    )
  })

  Object.keys(groupedInvestors).forEach((group) => {
    const id = `shareholder-types.plural.${group.toLowerCase()}`
    investors.push(
      <Select.OptGroup key={group} label={<FormattedMessage id={id} />}>
        {groupedInvestors[group]}
      </Select.OptGroup>,
    )
  })

  return investors
}

export const byTransactionEvents = (t1, t2) => {
  const i1 = t1.investor || t1.buyer || t1.receiver
  const i2 = t2.investor || t2.buyer || t2.receiver
  let diff = byStakeholders(i1, i2)
  if (diff !== 0) return diff

  if (t1.round && t2.round) {
    diff = date(t1.round.date).diff(date(t2.round.date))
    if (diff !== 0) return diff
  }

  return t1.amount - t2.amount
}

export const byProjects = (p1, p2) => date(p2.createdDate).diff(date(p1.createdDate))

export const byShares = (s1, s2) => {
  // Highest seniority is on top ( NOTE: Important as used in payout order - waterfall page )
  return s2.seniority - s1.seniority || s2.name.localeCompare(s1.name)
}

export const byStakeholders = (i1, i2) => {
  if (!i1.entryRound || !i2.entryRound) return byStakeholdersTypes(i1, i2)
  // Sort by round and then alphabetically. Investors that entered first, are listed on top
  return date(i1.entryRound.date).diff(date(i2.entryRound.date)) || byStakeholdersTypes(i1, i2)
}

export const byStakeholdersTypes = (i1, i2) => {
  return (
    shareholderOrder.findIndex((t) => t === i1.type) -
      shareholderOrder.findIndex((t) => t === i2.type) ||
    i1.name.localeCompare(i2.name) ||
    (i1.createdDate && i2.createdDate
      ? date(i2.createdDate).diff(date(i1.createdDate))
      : i1.id.localeCompare(i2.id))
  )
}

export const disabledDate = (previousRound, nextRound) => (d) => {
  return (
    !d ||
    (previousRound && !d.isAfter(previousRound.date)) ||
    (nextRound && !d.isBefore(nextRound.date))
  )
}

// sum of the shares an investor owns for a specified sharemode (e.g. ESOP = fully diluted, shares = LEGAL basis)
export const participationSumUp = (
  capTable,
  shareMode = { type: 'shares', excludeFounders: true },
  options = {
    mergeGrantsWithESOP: false,
    vestingOptions: null,
  },
) => {
  const _participationSumUp = {}
  capTable.participations.forEach((p) => {
    const {
      type,
      investor: { id },
    } = p
    if (!['SHARE', 'ESOP', 'GRANTED_SHARE'].includes(type)) return
    if (!_participationSumUp[id]) _participationSumUp[id] = 0

    if (type === 'GRANTED_SHARE' && options.mergeGrantsWithESOP && shareMode.type !== 'shares') {
      const esopGrantParticipations = summarizeGrantParticipationByEsops(p, options.vestingOptions)
      Object.values(esopGrantParticipations).forEach((e) => {
        if (!_participationSumUp[e.esopId]) _participationSumUp[e.esopId] = 0
        // NOTE: Do not apply vesting, as unvested shares remain in ESOP --> type:"esop"
        _participationSumUp[e.esopId] += getAmount(e, { ...shareMode, type: 'esop' })
      })
    } else {
      _participationSumUp[id] += getAmount(p, shareMode)
    }
  })
  return _participationSumUp
}

// Function will convert a GrantedParticipation into Partial-Participations for each ESOP-Pool
// Assumption: GrantedParticipations only have ADD entries -> SUB (e.g. buyout) is not possible
export const summarizeGrantParticipationByEsops = (grantParticipation, vestingOptions) => {
  const { entries, investor, share, id, type } = grantParticipation
  const esopsGrantEntries = groupBy(entries, (e) => get('event.esop.id', e))

  const esopGrantParticipations = {}
  esopsGrantEntries.forEach((esopGrantEntries, esopId) => {
    const { amountEsop, amountVested, amountExit } = esopGrantEntries.reduce(
      (t, e) => {
        if (e.operation !== 'ADD') return t // should not be not-possible
        t.amountEsop += e.amount
        t.amountVested += vestingOptions
          ? applyVesting(
              e.amount,
              e.event.vestingSchedule,
              e.event.round.date,
              vestingOptions.endDate,
            )
          : e.amount
        t.amountExit += vestingOptions
          ? applyVesting(
              e.amount,
              e.event.vestingSchedule,
              e.event.round.date,
              vestingOptions.endDate,
              true,
            )
          : e.amount
        return t
      },
      {
        amountEsop: 0,
        amountVested: 0,
        amountExit: 0,
      },
    )

    esopGrantParticipations[esopId] = {
      id: `grantParticipationId-${id}-esopId-${esopId}`,
      type,
      share,
      investor,
      entries: esopGrantEntries,
      esopId,
      quotaOfAllGranted: (amountEsop * 1.0) / grantParticipation.amount,
      amount: amountEsop,
      amountLegal: 0,
      amountEsop,
      amountVested,
      amountExit,
    }
  })

  return esopGrantParticipations
}

export const groupCnParticipationsByRoundAndNonEmpty = (
  cnParticipations,
  conversionInvestments,
) => {
  const cnTranchesGroupedByRound = Array.from(
    groupBy(cnParticipations, (p) => getCNInvestmentRound(p).id).values(),
  ).filter(
    (cnTranches) =>
      !equalsDouble(
        cnTranches.reduce((t, c) => t + c.amount, 0),
        0,
      ) &&
      !conversionInvestments.some((cc) =>
        cnTranches.some((c) => cc.convertibleNote.id === c.convertibleNote.id),
      ),
  )

  return cnTranchesGroupedByRound
}

export const getCNInvestment = (cnParticipation) => {
  const cnInvestment = cnParticipation.entries
    .map((e) => e.event)
    .find((e) => e.type === 'CN_INVESTMENT')
  return cnInvestment
}

export const getCNInvestmentRound = (cnParticipation) => {
  return getCNInvestment(cnParticipation).round
}

export const excludeCNTranchenAfterRound = (cnParticipation, round) => {
  // Exclude CN Investments (Tranches) which are after the round date
  // This is needed because the latest captable will be calculated on todays basis and otherwise includes them
  return date(getCNInvestment(cnParticipation).convertibleNote.dateOfDeposit).isAfter(
    date(round.date),
  )
}

export const getRoundInfos = (round, rounds) => {
  const roundIdx = rounds.findIndex((r) => r.id === round.id)
  const isFounding = roundIdx === 0
  const previousRound = isFounding ? null : rounds[roundIdx - 1]
  const nextRound = roundIdx === rounds.length - 1 ? null : rounds[roundIdx + 1]
  return { roundIdx, isFounding, previousRound, nextRound }
}

// *********************
// ****  VALIDATION ****
// *********************
export const validateShare = (shares, totalInvestments) => (rule, value) => {
  const validShare = (share, totalInvestments) => {
    if (
      share.liquidationPreference &&
      share.liquidationPreference.capType === 'INVESTMENT_RETURN' &&
      share.liquidationPreference.cap < totalInvestments * share.liquidationPreference.multiple
    )
      return false
    // extend share validation
    return true
  }
  const share = shares.find((s) => s.id === value)
  if (!share || !hasRemoteId(share) || (totalInvestments && !validShare(share, totalInvestments)))
    return Promise.reject(new Error('The share is not valid yet'))
  return Promise.resolve()
}

export const validateInputPercent = (required) => (rule, value) => {
  if ((required && !value) || (value && (!(typeof value === 'number') || parseFloat(value) < 0))) {
    return Promise.reject(new Error('Please enter a valid percentage greater or equal to zero！'))
  }
  return Promise.resolve()
}

export const validateInputNumber = (required, minimumValue = 1) => (rule, value) => {
  if (
    (required && !nonNull(value)) ||
    (value && (!(typeof value === 'number') || parseFloat(value) < minimumValue))
  ) {
    return Promise.reject(new Error('Please enter a valid input number greater zero！'))
  }
  return Promise.resolve()
}

export const validateMinimumCap = (totalInvestments, multiple) => (rule, value) => {
  if (!(typeof value === 'number') || parseFloat(value) < totalInvestments * multiple) {
    return Promise.reject(
      new Error(
        `Please enter a valid cap greater total Investments * multiple (${
          totalInvestments * multiple
        })`,
      ),
    )
  }
  return Promise.resolve()
}

export const validateVestingSchedule = () => (rule, schedule) => {
  if (
    !schedule ||
    (schedule.length > 0 &&
      schedule.every(
        (p) =>
          nonEmptyCellValue(p.percent) &&
          nonEmptyCellValue(p.months) &&
          nonEmptyCellValue(p.vestingTime),
      ))
  ) {
    return Promise.resolve()
  }
  return Promise.reject(new Error('The vesting schedule is not valid'))
}

export const validateRound = (round) => {
  if (!round) return false
  if (!hasRemoteId(round)) return false
  if (round.preMoneyValuation < 0) return false
  if (!round.name || round.name.length === 0) return false
  if (!round.share || !round.date) return false
  return true
}

export const validateCN = (cn) => {
  if (!cn) return false
  if (!cn.amount) return false
  if (!cn.convertibleNote) return false
  if (!cn.convertibleNote.maturityDate) return false
  return true
}

export const validateEmailInput = (required, translate) => (rule, value) => {
  if ((required && !value) || (value && !validateEmail(value))) {
    return Promise.reject(new Error(translate('groupPage.invite.error.emailInvalid')))
  }
  return Promise.resolve()
}

export const validateURI = (value) => {
  const prefix = 'https://'
  if (value.substr(0, prefix.length) !== prefix) {
    value = prefix + value
  }
  return new RegExp(
    'https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)',
    'g',
  ).test(value)
}

export const validateCapVsFloor = (cap, floor) => {
  const isValid = !nonNull(cap) || !nonNull(floor) || cap >= floor
  if (!isValid) {
    return Promise.reject(new Error('The cap needs to be equal or larger than the floor parameter'))
  }

  return Promise.resolve()
}
